export interface SignUpEventData {
  method: "google" | "linkedin" | "email";
}

type DataLayerEvent = {
  event?: string;
  ecommerce?: unknown;
  event_callback?: () => void;
  [key: string]: unknown;
};

const GTM_TIMEOUT_MS = 300;

function isGtmEnabled(): boolean {
  // Vite exposes env vars via import.meta.env at build time
  return import.meta.env.VITE_GTM_ENABLED === "true";
}

function ensureDataLayer(): DataLayerEvent[] | null {
  if (!("window" in globalThis)) return null;
  const w = window as unknown as { dataLayer?: DataLayerEvent[] };
  if (!w.dataLayer) {
    // If GTM is enabled, initialize the dataLayer array to avoid crashes
    w.dataLayer = [];
  }
  return w.dataLayer || null;
}

/**
 * Sends a GA4 sign_up event via GTM when a user successfully creates an account.
 * Tracks the authentication method used (google, linkedin, or email).
 */
export async function trackSignUp(params: SignUpEventData): Promise<void> {
  try {
    if (!isGtmEnabled()) {
      console.debug("[GTM] Disabled; sign_up payload:", params);
      return;
    }
    const dataLayer = ensureDataLayer();
    if (!dataLayer) return;

    await new Promise<void>((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve();
        }
      }, GTM_TIMEOUT_MS);

      const payload: DataLayerEvent = {
        event: "sign_up",
        method: params.method,
        event_callback: () => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        },
      };
      dataLayer.push(payload);
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[GTM] sign_up tracking failed:", err);
    }
  }
}

/**
 * Sends a GA4 email_verified event via GTM when a user verifies their email.
 * This tracks the completion of the email verification step for email signup users.
 */
export async function trackEmailVerified(): Promise<void> {
  try {
    if (!isGtmEnabled()) {
      console.debug("[GTM] Disabled; email_verified event");
      return;
    }
    const dataLayer = ensureDataLayer();
    if (!dataLayer) return;

    await new Promise<void>((resolve) => {
      let settled = false;
      const timer = setTimeout(() => {
        if (!settled) {
          settled = true;
          resolve();
        }
      }, GTM_TIMEOUT_MS);

      const payload: DataLayerEvent = {
        event: "email_verified",
        event_callback: () => {
          if (!settled) {
            settled = true;
            clearTimeout(timer);
            resolve();
          }
        },
      };
      dataLayer.push(payload);
    });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[GTM] email_verified tracking failed:", err);
    }
  }
}
